// TanStack Query hooks. Query keys are centralised in `keys` so that a mutation
// can invalidate exactly what it affected without guessing at key shapes.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { ApiError, api } from './client'
import type {
  AnswerResult,
  BulkImportResult,
  Direction,
  Entry,
  EntryPage,
  Language,
  LanguageStats,
  ArcadeOverview,
  ArcadeScoreResult,
  Lesson,
  LessonCompleteResult,
  MatchRound,
  PixelArt,
  PracticeCard,
  TokenWallet,
  Unit,
  User,
  Visibility,
} from './types'

export const keys = {
  me: ['me'] as const,
  languages: ['languages'] as const,
  language: (id: number) => ['language', id] as const,
  entries: (id: number, q: string) => ['entries', id, q] as const,
  publicLanguages: (q: string) => ['public-languages', q] as const,
  stats: (id: number) => ['stats', id] as const,
  practice: (id: number, direction: Direction | 'both') =>
    ['practice', id, direction] as const,
  units: (id: number) => ['units', id] as const,
  wallet: ['wallet'] as const,
  arcade: ['arcade'] as const,
  gallery: ['gallery'] as const,
}

// --- auth -----------------------------------------------------------------

export function useMe(options?: Partial<UseQueryOptions<User | null>>) {
  return useQuery<User | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me')
      } catch (error) {
        // Not being logged in is an expected state, not a failure to report.
        if (error instanceof ApiError && error.isUnauthorized) return null
        throw error
      }
    },
    // Auth state changes only through explicit login/logout, both of which
    // update the cache directly.
    staleTime: Infinity,
    retry: false,
    ...options,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<User>('/auth/login', body),
    onSuccess: (user) => {
      qc.setQueryData(keys.me, user)
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      password: string
      display_name: string
    }) => api.post<User>('/auth/register', body),
    onSuccess: (user) => qc.setQueryData(keys.me, user),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    // Drop everything: the whole cache belonged to the user who just left.
    onSuccess: () => qc.clear(),
  })
}

// --- languages ------------------------------------------------------------

export function useLanguages() {
  return useQuery({
    queryKey: keys.languages,
    queryFn: () => api.get<Language[]>('/languages'),
  })
}

export function useLanguage(id: number) {
  return useQuery({
    queryKey: keys.language(id),
    queryFn: () => api.get<Language>(`/languages/${id}`),
    enabled: Number.isFinite(id),
  })
}

export function usePublicLanguages(q: string) {
  return useQuery({
    queryKey: keys.publicLanguages(q),
    queryFn: () =>
      api.get<Language[]>(
        `/languages/public${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
  })
}

export function useCreateLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      description?: string
      source_language?: string
      visibility?: Visibility
    }) => api.post<Language>('/languages', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

export function useUpdateLanguage(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      body: Partial<
        Pick<
          Language,
          'name' | 'description' | 'visibility' | 'pronunciation_locale' | 'icon_art_id'
        >
      >,
    ) =>
      api.patch<Language>(`/languages/${id}`, body),
    onSuccess: (language) => {
      qc.setQueryData(keys.language(id), language)
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useDeleteLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/languages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

export function useCopyLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Language>(`/languages/${id}/copy`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

// --- entries --------------------------------------------------------------

export function useEntries(languageId: number, q: string) {
  return useQuery({
    queryKey: keys.entries(languageId, q),
    queryFn: () =>
      api.get<EntryPage>(
        `/languages/${languageId}/entries?limit=500${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: Number.isFinite(languageId),
  })
}

/** Invalidates everything derived from a language's word list. */
function useEntryMutation<TArgs>(
  languageId: number,
  fn: (args: TArgs) => Promise<unknown>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries', languageId] })
      qc.invalidateQueries({ queryKey: keys.stats(languageId) })
      qc.invalidateQueries({ queryKey: keys.language(languageId) })
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useCreateEntry(languageId: number) {
  return useEntryMutation(languageId, (body: { source_text: string; target_text: string }) =>
    api.post<Entry>(`/languages/${languageId}/entries`, body),
  )
}

export function useUpdateEntry(languageId: number) {
  return useEntryMutation(
    languageId,
    ({ id, ...body }: { id: number; source_text?: string; target_text?: string }) =>
      api.patch<Entry>(`/entries/${id}`, body),
  )
}

export function useDeleteEntry(languageId: number) {
  return useEntryMutation(languageId, (id: number) => api.delete<void>(`/entries/${id}`))
}

export function useBulkImport(languageId: number) {
  return useEntryMutation(languageId, (text: string) =>
    api.post<BulkImportResult>(`/languages/${languageId}/entries/bulk`, { text }),
  ) as ReturnType<typeof useMutation<BulkImportResult, Error, string>>
}

// --- practice -------------------------------------------------------------

export function usePracticeQueue(languageId: number, direction: Direction | 'both') {
  return useQuery({
    queryKey: keys.practice(languageId, direction),
    queryFn: () =>
      api.get<PracticeCard[]>(
        `/languages/${languageId}/practice/next?limit=20${
          direction === 'both' ? '' : `&direction=${direction}`
        }`,
      ),
    enabled: Number.isFinite(languageId),
    // A session should always start from the server's current queue rather
    // than a queue cached from a previous visit.
    staleTime: 0,
    gcTime: 0,
  })
}

export function useAnswer() {
  return useMutation({
    mutationFn: (body: {
      entry_id: number
      direction: Direction
      given?: string
      grade?: number
    }) => api.post<AnswerResult>('/practice/answer', body),
  })
}

// --- lessons --------------------------------------------------------------

export function useUnits(languageId: number) {
  return useQuery({
    queryKey: keys.units(languageId),
    queryFn: () => api.get<Unit[]>(`/languages/${languageId}/units`),
    enabled: Number.isFinite(languageId),
  })
}

/**
 * Fetch one lesson. Deliberately not a `useQuery`: a lesson is generated fresh
 * each time, and caching one would silently replay the same twelve exercises.
 */
export function useLoadLesson(languageId: number) {
  return useMutation({
    mutationFn: (unitIndex: number) =>
      api.get<Lesson>(`/languages/${languageId}/units/${unitIndex}/lesson`),
  })
}

export function useCompleteLesson(languageId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      unitIndex,
      correct,
      total,
    }: {
      unitIndex: number
      correct: number
      total: number
    }) =>
      api.post<LessonCompleteResult>(
        `/languages/${languageId}/units/${unitIndex}/complete`,
        { correct, total },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.units(languageId) })
      qc.invalidateQueries({ queryKey: keys.stats(languageId) })
      qc.invalidateQueries({ queryKey: keys.wallet })
    },
  })
}

export function useRenameUnit(languageId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ unitIndex, name }: { unitIndex: number; name: string }) =>
      api.patch<Unit>(`/languages/${languageId}/units/${unitIndex}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.units(languageId) }),
  })
}

export function useMoveEntry(languageId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      unitIndex,
      sortOrder,
    }: {
      id: number
      unitIndex: number
      sortOrder: number
    }) =>
      api.patch<void>(`/entries/${id}/placement`, {
        unit_index: unitIndex,
        sort_order: sortOrder,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.units(languageId) })
      qc.invalidateQueries({ queryKey: ['entries', languageId] })
    },
  })
}

// --- vocabulary games -----------------------------------------------------

export function useMatchRound(languageId: number, size: number) {
  return useQuery({
    queryKey: ['match', languageId, size],
    queryFn: () =>
      api.get<MatchRound>(`/languages/${languageId}/games/match?size=${size}`),
    enabled: Number.isFinite(languageId),
    // Every round should be a fresh deal, never a replay of a cached one.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })
}

export function useGameFeed(languageId: number, limit: number) {
  return useQuery({
    queryKey: ['game-feed', languageId, limit],
    queryFn: () =>
      api.get<PracticeCard[]>(`/languages/${languageId}/games/feed?limit=${limit}`),
    enabled: Number.isFinite(languageId),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })
}

// --- arcade ---------------------------------------------------------------

export function useArcade() {
  return useQuery({
    queryKey: keys.arcade,
    queryFn: () => api.get<ArcadeOverview>('/arcade'),
  })
}

export function useStartPlay() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (game: string) =>
      api.post<{ was_free: boolean; balance: number }>(`/arcade/${game}/start`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.arcade })
      qc.invalidateQueries({ queryKey: keys.wallet })
    },
  })
}

export function useRecordScore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ game, score }: { game: string; score: number }) =>
      api.post<ArcadeScoreResult>(`/arcade/${game}/score`, { score }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.arcade }),
  })
}

export function useUnlockPixelPaint() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ was_free: boolean; balance: number }>('/arcade/pixel-paint/unlock'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.arcade })
      qc.invalidateQueries({ queryKey: keys.wallet })
    },
  })
}

export function useGallery() {
  return useQuery({
    queryKey: keys.gallery,
    queryFn: () => api.get<PixelArt[]>('/arcade/pixel-paint/gallery'),
  })
}

export function useSaveDrawing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      width: number
      height: number
      pixels: string
    }) => api.post<PixelArt>('/arcade/pixel-paint/gallery', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.gallery }),
  })
}

export function useDeleteDrawing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(`/arcade/pixel-paint/gallery/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.gallery })
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

// --- tokens ---------------------------------------------------------------

export function useWallet(enabled = true) {
  return useQuery({
    queryKey: keys.wallet,
    queryFn: () => api.get<TokenWallet>('/tokens'),
    // Signed-out visitors have no wallet; asking would just 401 on every page.
    enabled,
  })
}

export function useStats(languageId: number) {
  return useQuery({
    queryKey: keys.stats(languageId),
    queryFn: () => api.get<LanguageStats>(`/languages/${languageId}/stats`),
    enabled: Number.isFinite(languageId),
  })
}
